import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { commentsApi, documentsApi } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader } from './ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Reply, 
  MoreVertical, 
  CheckCircle2, 
  Circle,
  Send,
  Trash2,
  Edit,
  AtSign,
  Filter,
  Check,
  CheckCheck,
  Users,
  Eye,
  X,
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  Download,
  UserPlus,
  Upload,
  Paperclip,
  AlertTriangle,
  Loader2,
  Lock,
  BadgeCheck,
  Layers,
  Info,
  CloudUpload,
  Sparkles,
  ShieldAlert,
  ShieldCheck
} from 'lucide-react';
import { formatDateTime, getRoleLabel, getRoleColor } from '../lib/utils';

// Document Status Badge Component
const DocumentStatusBadge = ({ status }) => {
  const statusConfig = {
    PENDING_REVIEW: { label: 'Pending Review', color: 'bg-blue-100 text-blue-700', icon: '🔵' },
    CHANGES_REQUESTED: { label: 'Changes Requested', color: 'bg-amber-100 text-amber-700', icon: '🟡' },
    APPROVED: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: '🟢' },
  };
  const config = statusConfig[status] || statusConfig.PENDING_REVIEW;
  return (
    <Badge className={`${config.color} text-xs`}>
      {config.icon} {config.label}
    </Badge>
  );
};

// Seen Status Indicator Component
function SeenIndicator({ comment, currentUserId, hideForCompany = false }) {
  const [showSeenList, setShowSeenList] = useState(false);
  
  // Hide seen indicator for company accounts
  if (hideForCompany) return null;
  
  const seenBy = comment.seen_by || [];
  
  // Deduplicate seen_by list by user_id
  const uniqueSeenBy = seenBy.reduce((acc, seen) => {
    if (!acc.find(s => s.user_id === seen.user_id)) {
      acc.push(seen);
    }
    return acc;
  }, []);
  
  // Don't show indicator if no one has seen
  if (uniqueSeenBy.length === 0) return null;
  
  return (
    <Popover open={showSeenList} onOpenChange={setShowSeenList}>
      <PopoverTrigger asChild>
        <button 
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-slate-600"
          data-testid={`seen-indicator-${comment.id}`}
        >
          <CheckCheck className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="end">
        <div className="text-sm font-medium mb-2">Seen by</div>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {uniqueSeenBy.map((seen, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm py-1">
              <Eye className="w-3 h-3 text-slate-400" />
              <span>{seen.user_name}</span>
              <span className="text-xs text-slate-400 ml-auto">
                {new Date(seen.seen_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Single Message within a Thread
function MessageItem({ message, onResolve, onDelete, onEdit, onMarkSeen, onDownloadAttachment, onPreviewDoc, onEditAttachmentVersion, currentUserId, isThreadResolved, documentVersions = [], isOverrideRole = false, hideSeenForCompany = false, isLatestVersion = false }) {
  const { isAdmin, isCompanyRep } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [showVersionEdit, setShowVersionEdit] = useState(false);
  const [editingVersion, setEditingVersion] = useState(message.attachment_related_version);
  const canEdit = message.user_id === currentUserId;
  const canDelete = message.user_id === currentUserId || isAdmin();
  
  useEffect(() => {
    const visibleTo = message.visible_to || [];
    const seenBy = message.seen_by || [];
    const alreadySeen = seenBy.some(s => s.user_id === currentUserId);
    
    // Mark as seen if user is in visible_to OR has override role (Admin/Chairman/CRD/Coordinator)
    const canSee = visibleTo.includes(currentUserId) || isOverrideRole;
    
    if (canSee && !alreadySeen && message.user_id !== currentUserId) {
      onMarkSeen(message.id);
    }
  }, [message.id, message.visible_to, message.seen_by, currentUserId, onMarkSeen, isOverrideRole]);

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;
    await onEdit(message.id, editContent);
    setIsEditing(false);
  };

  // Highlight @mentions in content
  const renderContent = (text) => {
    let result = text;
    if (message.mention_names && message.mention_names.length > 0) {
      message.mention_names.forEach(name => {
        const regex = new RegExp(`@${name}`, 'gi');
        result = result.replace(regex, `<span class="bg-amber-100 text-amber-800 px-1 rounded">@${name}</span>`);
      });
    }
    
    const groupMentions = ['@all', '@internal', '@Reviewers', '@PIs', '@Consultants'];
    groupMentions.forEach(group => {
      const regex = new RegExp(group, 'gi');
      result = result.replace(regex, `<span class="bg-blue-100 text-blue-800 px-1 rounded">${group}</span>`);
    });
    
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  const isResolved = message.is_resolved || isThreadResolved;
  const attachmentList = Array.isArray(message.attachments) && message.attachments.length > 0
    ? message.attachments
    : (message.attachment_id && message.attachment_filename
        ? [{ doc_id: message.attachment_doc_id || message.attachment_id, filename: message.attachment_filename, related_version: message.attachment_related_version }]
        : []);
  const hasAttachment = attachmentList.length > 0;

  return (
    <div 
      className={`p-3 rounded-lg ${isResolved ? 'bg-slate-100 opacity-70' : 'bg-white'} border border-slate-200 transition-all duration-300`}
      data-comment-id={message.id}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* iter138 — autonomous "AI Coordinator Agent" gets a distinctive gradient
              avatar with a Sparkles glyph + animated ring so operators feel the
              platform is autonomously orchestrating the workflow. */}
          {message.user_role === 'COORDINATOR_AGENT' ? (
            <div
              className="relative w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center text-white shadow-md ring-2 ring-purple-300/60"
              data-testid={`message-${message.id}-agent-avatar`}
              title="ClinicalFlow Agent"
            >
              <Sparkles className="w-4 h-4" />
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white animate-pulse" />
            </div>
          ) : (
            <div className="w-7 h-7 rounded-full bg-blue-900 flex items-center justify-center text-white text-xs font-medium">
              {message.user_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className={`font-medium text-sm ${message.user_role === 'COORDINATOR_AGENT' ? 'bg-gradient-to-r from-indigo-700 via-purple-700 to-pink-700 bg-clip-text text-transparent' : 'text-slate-900'}`}>
                {message.user_name}
              </span>
              <Badge className={`text-xs ${getRoleColor(message.user_role)} ${message.user_role === 'COORDINATOR_AGENT' ? 'gap-1' : ''}`}>
                {message.user_role === 'COORDINATOR_AGENT' && <Sparkles className="w-2.5 h-2.5" />}
                {getRoleLabel(message.user_role)}
              </Badge>
              {message.is_resolved && !isThreadResolved && (
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Resolved</Badge>
              )}
            </div>
            <span className="text-xs text-slate-500">{formatDateTime(message.created_at)}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <SeenIndicator comment={message} currentUserId={currentUserId} hideForCompany={hideSeenForCompany} />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <MoreVertical className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit && (
                <DropdownMenuItem onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem onClick={() => onDelete(message.id)} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={2} />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveEdit}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditContent(message.content); setIsEditing(false); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div>
          {/* iter120 — distinguishes system markers by color:
              • Coordinator handover (📨 …) → yellow
              • Assignment / approval markers           → green
              • Everything else                          → default slate */}
          {message.content?.startsWith('📨 Coordinator handover:') ? (
            <p
              className="text-sm text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded whitespace-pre-wrap"
              data-testid={`message-${message.id}-handover-marker`}
            >
              {message.content}
            </p>
          ) : message.is_partial_approval ? (
            // iter137 — partial-approval marker is GREEN (per user request) to match the
            // success theme. ✅ prefix removed from backend content. e.g.
            // "Approved by: Dr. X • 1 of 3 assignees".
            <p
              className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded whitespace-pre-wrap"
              data-testid={`message-${message.id}-partial-approval`}
            >
              {message.content}
            </p>
          ) : message.is_system_message ? (
            // iter136 — final-approval system banner ("✅ Document thread fully approved…")
            // gets the prominent GREEN BANNER treatment so it visually announces completion.
            // Other system messages (nudges, etc.) retain the slate-blue style.
            // iter137 — render @mentions via renderContent so they appear as clickable
            // chips (same as a normal user comment); recipients TO: block appears below.
            message.content?.startsWith('✅ Document thread fully approved') ? (
              <div
                className="text-sm font-medium text-emerald-900 bg-gradient-to-r from-emerald-100 to-emerald-50 border-l-4 border-emerald-500 px-3 py-2 rounded shadow-sm whitespace-pre-wrap"
                data-testid={`message-${message.id}-final-approval-banner`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Thread fully approved</span>
                </div>
                {renderContent(message.content)}
              </div>
            ) : (
              <p
                className="text-sm text-sky-800 bg-sky-50 border border-sky-200 px-2 py-1 rounded whitespace-pre-wrap"
                data-testid={`message-${message.id}-system-marker`}
              >
                {renderContent(message.content)}
              </p>
            )
          ) : message.content?.startsWith('Approved (override) by:') ? (
            // iter137 — override marker now renders exactly like a normal user comment
            // (plain slate text with clickable @mentions) so the TO: recipients chip
            // block below it shows the bypassed reviewers in the standard chip style.
            <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid={`message-${message.id}-override-approval`}>
              {renderContent(message.content)}
            </p>
          ) : (message.content?.startsWith('Assigned to:') || message.content?.startsWith('Approved by:')) ? (
            (() => {
              // iter158 — Role-masked render of "Assigned to: …" for Company /
              // Company Rep viewers. Group assignees by role; show Coordinator
              // names + role explicitly, aggregate other roles to "N <Role>s".
              const isAssignmentMsg = message.content?.startsWith('Assigned to:');
              if (isAssignmentMsg && isCompanyRep() && Array.isArray(message.assignees_meta) && message.assignees_meta.length > 0) {
                const byRole = {};
                for (const a of message.assignees_meta) {
                  const r = a.role || 'UNKNOWN';
                  (byRole[r] ||= []).push(a);
                }
                const roleLabel = (r) => (r || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                const pluralise = (label, n) => (n === 1 ? label : (label.endsWith('s') ? label : `${label}s`));
                const segments = [];
                // Coordinator(s) first — names visible.
                if (byRole['COORDINATOR']) {
                  const names = byRole['COORDINATOR'].map(c => c.name).join(', ');
                  segments.push(`Coordinator ${names}`);
                  delete byRole['COORDINATOR'];
                }
                // Remaining roles: count + role label.
                Object.keys(byRole).sort().forEach(r => {
                  const n = byRole[r].length;
                  segments.push(`${n} ${pluralise(roleLabel(r), n)}`);
                });
                return (
                  <p className="text-sm text-emerald-700 bg-emerald-50 px-2 py-1 rounded whitespace-pre-wrap" data-testid={`assignment-masked-${message.id}`}>
                    Assigned to: {segments.join(', ')}
                  </p>
                );
              }
              return <p className="text-sm text-emerald-700 bg-emerald-50 px-2 py-1 rounded whitespace-pre-wrap">{message.content}</p>;
            })()
          ) : (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{renderContent(message.content)}</p>
          )}
          {/* Recipients (picked via @ or the Recipients button) with their role chip */}
          {message.mention_users && message.mention_users.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`message-${message.id}-recipients`}>
              <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">To:</span>
              {message.mention_users.map((mu) => (
                <span
                  key={mu.id}
                  className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-full pl-2 pr-1 py-0.5 text-xs text-slate-700"
                  data-testid={`message-recipient-${mu.id}`}
                >
                  {mu.name}
                  <Badge className={`text-[10px] px-1.5 py-0 ${getRoleColor(mu.role)}`}>
                    {getRoleLabel(mu.role)}
                  </Badge>
                </span>
              ))}
            </div>
          )}
          {/* Attachment preview button with version info (iter108: Preview instead of Download
              — opens the unified DocumentPreviewModal which also has a Download button) */}
          {hasAttachment && (
            <div className="mt-2 flex items-center gap-2 flex-wrap min-w-0">
              {/* iter117 — render ONE preview button per attachment in the list.
                  For single-attachment comments this renders one button (same as
                  before). For multi-attachment comments (new in iter117), users
                  see all files inline under a single message. */}
              {attachmentList.map((att, idx) => (
                <Button
                  key={`${message.id}-att-${idx}`}
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1 max-w-full"
                  onClick={() => {
                    const docId = att.doc_id;
                    if (onPreviewDoc) onPreviewDoc(docId);
                    else if (onDownloadAttachment) onDownloadAttachment(docId);
                  }}
                  title={att.filename}
                  data-testid={idx === 0 ? `message-preview-${message.id}` : `message-preview-${message.id}-${idx}`}
                >
                  <Eye className="w-3 h-3 shrink-0" />
                  <span className="truncate max-w-[20rem] md:max-w-[28rem] lg:max-w-[40rem]">{att.filename}</span>
                </Button>
              ))}
              {/* iter109 — chip identifying the attachment kind:
                  • is_version_marker (system "📎 Version N uploaded:" row) → "New Version v<N>" (or "Version v<N>" if not latest, iter117)
                  • everything else                                          → "Attachment" */}
              {message.is_version_marker ? (
                <>
                  <Badge
                    className={`text-[10px] gap-1 border ${isLatestVersion ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-700 border-slate-300'}`}
                    data-testid={`message-version-chip-${message.id}`}
                  >
                    <Layers className="w-3 h-3" />
                    {isLatestVersion ? 'New Version' : 'Version'} v{message.attachment_related_version || '?'}
                  </Badge>
                  {isLatestVersion && (
                    <Badge
                      className="text-[10px] bg-indigo-600 text-white border border-indigo-700 gap-1"
                      data-testid={`message-latest-version-chip-${message.id}`}
                    >
                      <BadgeCheck className="w-3 h-3" /> Latest version
                    </Badge>
                  )}
                </>
              ) : (
                /* iter111 — combined chip: "v<N> attachment" (was separate "Attachment" + "for v<N>") */
                <Badge
                  variant="outline"
                  className="text-[10px] border-slate-300 bg-slate-50 text-slate-700 gap-1"
                  data-testid={`message-attachment-chip-${message.id}`}
                >
                  <Paperclip className="w-3 h-3" />
                  {message.attachment_related_version ? `v${message.attachment_related_version} attachment` : 'Attachment'}
                </Badge>
              )}
              {/* iter111 — Change Version is only meaningful for ordinary attachments;
                  hide it on system version_marker rows where v<N> is already the file. */}
              {canEdit && onEditAttachmentVersion && message.attachment_doc_id && !message.is_version_marker && (
                <Popover open={showVersionEdit} onOpenChange={setShowVersionEdit}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 text-xs px-2">
                      <Edit className="w-3 h-3 mr-1" />
                      Change Version
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-3" align="start">
                    <div className="space-y-2">
                      <Label className="text-xs">Related to version:</Label>
                      <Select 
                        value={editingVersion?.toString()} 
                        onValueChange={(val) => setEditingVersion(parseInt(val))}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Select version" />
                        </SelectTrigger>
                        <SelectContent>
                          {documentVersions.map(v => (
                            <SelectItem key={v.version} value={v.version.toString()}>
                              Version {v.version}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          className="h-7 text-xs flex-1"
                          onClick={async () => {
                            await onEditAttachmentVersion(message.attachment_doc_id, editingVersion);
                            setShowVersionEdit(false);
                          }}
                        >
                          Save
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs"
                          onClick={() => {
                            setEditingVersion(message.attachment_related_version);
                            setShowVersionEdit(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Comment Input Component with optional file attachment
function CommentInput({ onSubmit, onAttachFile, mentionData, placeholder = "Write a message...", compact = false, isDocumentThread = false, documentVersions = [], latestVersion = 1, prefilledContent = '', onCancel = null, autoMentionOnAttach = null }) {
  const [content, setContent] = useState(prefilledContent);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  // Search & role filter shared by Recipients popover and @-mention dropdown
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showVisibilityWarning, setShowVisibilityWarning] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [showEmptyRecipientsWarning, setShowEmptyRecipientsWarning] = useState(false);
  const [pendingEmptySubmit, setPendingEmptySubmit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // iter161 — user can dismiss the "Uploader @X will be notified." notice with
  // a small red X. Dismissal removes the uploader from selectedMentions, strips
  // any inline @name prefix from the textarea, and suppresses re-adding on
  // subsequent attaches.
  const [autoMentionDismissed, setAutoMentionDismissed] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevPrefilledRef = useRef(prefilledContent);
  
  // Sync prefilled content when it changes (from empty to non-empty)
  useEffect(() => {
    // Only update if prefilledContent actually changed and is non-empty
    if (prefilledContent && prefilledContent !== prevPrefilledRef.current) {
      prevPrefilledRef.current = prefilledContent;
      // Use a microtask to avoid the lint warning about synchronous setState
      queueMicrotask(() => {
        setContent(prefilledContent);
        textareaRef.current?.focus();
      });
    } else if (!prefilledContent && prevPrefilledRef.current) {
      // Reset when prefilled content is cleared
      prevPrefilledRef.current = '';
    }
  }, [prefilledContent]);
  
  const users = mentionData?.users || [];
  const groups = mentionData?.groups || [];
  const canMentionAnyone = mentionData?.can_mention_anyone || false;
  const isPrivileged = mentionData?.is_privileged || false;
  const threadVisibleTo = mentionData?.thread_visible_to || [];
  
  // Get users who are outside thread visibility
  const getOutsideVisibilityUsers = () => {
    return selectedMentions
      .map(id => users.find(u => u.id === id))
      .filter(u => u && u.outside_thread_visibility === true);
  };
  
  // Compute default version for file attachments
  const defaultVersion = documentVersions.length > 0 
    ? Math.max(...documentVersions.map(v => v.version)) 
    : latestVersion;

  const handleSubmit = () => {
    if (!content.trim() && selectedFiles.length === 0) return;

    // Parse @mentions from text → user ids (groups are intentionally removed in this UI).
    //
    // iter167 Issue 17 — Two distinct users can legitimately share the same first+last
    // name (e.g. "Asmaa Alsawadi" the Coordinator vs "Asmaa Alsawadi (MH)" the Company
    // Rep). The previous regex `/@(\w+(?:\s+\w+)?)/g` could not match parens in display
    // names, so `@Asmaa Alsawadi (MH)` was truncated to `Asmaa Alsawadi` and `users.find`
    // returned the *first* user with that name — silently adding the wrong person
    // (Coordinator) to the recipients (TO:) line of the reply.
    //
    // New approach: match against each user's EXACT `@<name>` literal in the content,
    // longest-name-first, and skip ranges that overlap a longer match. This guarantees:
    //   • "@Asmaa Alsawadi (MH)" → matches ONLY the Company Rep
    //   • "@Asmaa Alsawadi"      → matches ONLY the Coordinator
    //   • Same-named users are never auto-substituted via prefix collision.
    const mentions = [...selectedMentions];
    const sortedUsers = [...users]
      .filter(u => u && u.name)
      .sort((a, b) => b.name.length - a.name.length);
    const matchedRanges = []; // [start,end) tuples to prevent overlapping matches
    for (const u of sortedUsers) {
      const token = `@${u.name}`;
      let from = 0;
      while (true) {
        const idx = content.indexOf(token, from);
        if (idx === -1) break;
        const end = idx + token.length;
        const overlaps = matchedRanges.some(([s, e]) => idx < e && end > s);
        if (!overlaps) {
          matchedRanges.push([idx, end]);
          if (!mentions.includes(u.id)) mentions.push(u.id);
        }
        from = idx + 1;
      }
    }

    // iter163 — If the user explicitly dismissed the "Uploader will be notified"
    // notice, scrub the uploader's id from the final mentions and strip any
    // residual @name token from the body so the comment doesn't leak a
    // "TO: <CompanyRep>" block when sent.
    let safeContent = content;
    let cleanedMentions = mentions;
    if (autoMentionDismissed && autoMentionOnAttach?.id) {
      cleanedMentions = mentions.filter((mid) => mid !== autoMentionOnAttach.id);
      if (autoMentionOnAttach.name) {
        // iter167 Issue 16 — escape regex metachars in the display name (e.g. parens in "(MH)").
        const escaped = autoMentionOnAttach.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        safeContent = (content || '').replace(
          new RegExp(`@${escaped}\\s?`, 'g'),
          ''
        );
      }
    }

    // Empty-recipients confirmation gate (Q): if the user is about to send a message
    // without any @mention and no selected recipient, ask for explicit confirmation
    // so they know nobody gets an immediate notification.
    if (cleanedMentions.length === 0) {
      setPendingEmptySubmit({ content: safeContent });
      setShowEmptyRecipientsWarning(true);
      return;
    }

    // Check if privileged user is mentioning users outside thread visibility
    const outsideUsers = cleanedMentions
      .map(id => users.find(u => u.id === id))
      .filter(u => u && u.outside_thread_visibility === true);

    if (isDocumentThread && isPrivileged && outsideUsers.length > 0) {
      setPendingSubmit({ content: safeContent, mentions: cleanedMentions, mentionGroups: [], outsideUsers });
      setShowVisibilityWarning(true);
      return;
    }

    executeSubmit(safeContent, cleanedMentions, []);
  };
  
  const executeSubmit = async (content, mentions, mentionGroups) => {
    setSubmitting(true);
    try {
      // iter163 — Defensive filter: if the user explicitly dismissed the auto-mention
      // notice for the uploader, make sure the uploader's id is NEVER included in
      // the final mentions payload even if it leaked back in via a stale closure or
      // a duplicate parsing pass. Same for inline @name token in the content body.
      let safeContent = content;
      let safeMentions = mentions;
      if (autoMentionDismissed && autoMentionOnAttach?.id) {
        safeMentions = (mentions || []).filter((mid) => mid !== autoMentionOnAttach.id);
        if (autoMentionOnAttach.name) {
          // iter167 Issue 16 — escape regex metachars (e.g. parens in "(MH)").
          const escaped = autoMentionOnAttach.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          safeContent = (content || '').replace(
            new RegExp(`@${escaped}\\s?`, 'g'),
            ''
          );
        }
      }
      // If there are file(s), call onAttachFile with the full array + selected version
      if (selectedFiles.length > 0 && onAttachFile) {
        const versionToUse = selectedVersion ?? defaultVersion;
        // iter167 Issue 16 — Pass `notifyUploader=false` when the user
        // dismissed the auto-mention chip so the backend doesn't re-add
        // the uploader to mentions / visible_to silently.
        const notifyUploader = !(autoMentionOnAttach && autoMentionDismissed);
        await onAttachFile(safeContent, safeMentions, mentionGroups, selectedFiles, versionToUse, notifyUploader);
      } else {
        await onSubmit(safeContent, safeMentions, mentionGroups);
      }
      setContent('');
      setSelectedMentions([]);
      setSelectedFiles([]);
      setSelectedVersion(null);
      setShowVisibilityWarning(false);
      setPendingSubmit(null);
      setShowEmptyRecipientsWarning(false);
      setPendingEmptySubmit(null);
      // iter167 Issue 16 — Reset the dismissed flag so the next reply
      // gets a fresh auto-mention notice (avoids the "no @ but TO: appears"
      // ghost-state where the previous reply's dismissal carried over).
      setAutoMentionDismissed(false);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleConfirmVisibilityExpansion = () => {
    if (pendingSubmit) {
      executeSubmit(pendingSubmit.content, pendingSubmit.mentions, pendingSubmit.mentionGroups);
    }
  };
  
  const handleCancelVisibilityExpansion = () => {
    setShowVisibilityWarning(false);
    setPendingSubmit(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
    if (e.key === '@') {
      setShowMentions(true);
      setMentionSearch('');
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setContent(value);

    // Only the @-fragment being actively typed (up to the caret) drives the dropdown
    const caret = e.target.selectionStart ?? value.length;
    const beforeCaret = value.substring(0, caret);
    const lastAtIndex = beforeCaret.lastIndexOf('@');
    if (lastAtIndex === -1) {
      setShowMentions(false);
      return;
    }
    const afterAt = beforeCaret.substring(lastAtIndex + 1);
    // Close once the fragment spans a new line, already completed a known mention,
    // or ran past two words — otherwise the dropdown sticks around forever.
    const completedMention = users.some(
      (u) => afterAt.toLowerCase().startsWith(`${u.name.toLowerCase()} `)
        || afterAt.toLowerCase() === u.name.toLowerCase() + '\n'
    );
    if (afterAt.includes('\n') || completedMention || afterAt.split(' ').length > 2) {
      setShowMentions(false);
    } else {
      setShowMentions(true);
      setMentionSearch(afterAt.toLowerCase());
    }
  };

  const insertMention = (item) => {
    const lastAtIndex = content.lastIndexOf('@');
    const newContent = content.substring(0, lastAtIndex) + `@${item.name} `;
    setContent(newContent);
    setShowMentions(false);
    setSelectedMentions([...selectedMentions, item.id]);
    textareaRef.current?.focus();
  };

  const toggleRecipient = (userId) => {
    const user = users.find(u => u.id === userId);
    if (selectedMentions.includes(userId)) {
      // Remove from selected mentions
      setSelectedMentions(selectedMentions.filter(id => id !== userId));
      // Also remove @username from content if present
      if (user) {
        const mentionPattern = new RegExp(`@${user.name}\\s?`, 'gi');
        setContent(prev => prev.replace(mentionPattern, ''));
      }
    } else {
      // Add to selected mentions
      setSelectedMentions([...selectedMentions, userId]);
      // Insert @username into the text field
      if (user) {
        setContent(prev => {
          const trimmed = prev.trimEnd();
          const separator = trimmed.length > 0 ? ' ' : '';
          return `${trimmed}${separator}@${user.name} `;
        });
        textareaRef.current?.focus();
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSelectedFiles((prev) => {
      // Append, dedupe by name+size so the same file can't be added twice
      const seen = new Set(prev.map((f) => `${f.name}_${f.size}`));
      const next = [...prev];
      files.forEach((f) => {
        const key = `${f.name}_${f.size}`;
        if (!seen.has(key)) {
          next.push(f);
          seen.add(key);
        }
      });
      return next;
    });
    // iter115 — auto-mention uploader on first attach
    // iter161 — skip when the user explicitly dismissed the auto-mention.
    if (autoMentionOnAttach?.id && !autoMentionDismissed && !selectedMentions.includes(autoMentionOnAttach.id)) {
      setSelectedMentions((prev) => prev.includes(autoMentionOnAttach.id) ? prev : [...prev, autoMentionOnAttach.id]);
    }
    // iter117 — Pre-fill the textarea with ONE "📎 Attached: <filename>" line PER
    // file (multi-line). All files post to ONE comment in a single multipart.
    setContent((prev) => {
      const buildTags = (fileList) => fileList.map((f) => `📎 Attached: ${f.name}`).join('\n');
      const mention = (autoMentionOnAttach?.name && !autoMentionDismissed) ? `@${autoMentionOnAttach.name} ` : '';
      const hasMention = mention && prev && prev.includes(`@${autoMentionOnAttach.name}`);
      const prefix = (mention && !hasMention) ? mention : '';
      // Collect the union of already-selected files + new ones for the tag block
      const allFiles = (() => {
        const seen = new Set(selectedFiles.map((f) => `${f.name}_${f.size}`));
        const next = [...selectedFiles];
        files.forEach((f) => {
          const key = `${f.name}_${f.size}`;
          if (!seen.has(key)) { next.push(f); seen.add(key); }
        });
        return next;
      })();
      const tags = buildTags(allFiles);
      // Strip any prior 📎 Attached lines, then append the fresh block
      const cleaned = (prev || '').replace(/(?:^|\n)📎 Attached:[^\n]*/g, '').trimEnd();
      if (cleaned) return `${prefix}${cleaned}\n\n${tags}`;
      return `${prefix}${tags}`;
    });
    // Reset the input so picking the SAME file again triggers onChange
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      // Update the attach tag inline in content (one line per file)
      setContent((curr) => {
        const cleaned = (curr || '').replace(/(?:^|\n)📎 Attached:[^\n]*/g, '').trimEnd();
        if (next.length === 0) {
          return cleaned;
        }
        const tags = next.map((f) => `📎 Attached: ${f.name}`).join('\n');
        return cleaned ? `${cleaned}\n\n${tags}` : tags;
      });
      return next;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleGroup = () => {
    // Group broadcasts are intentionally removed from this UI per product decision.
    // Users must mention individuals one-by-one for a cleaner audit trail.
  };

  // Combined filter: text query (from popover search OR @mention search) + role chip
  const normalizedQuery = (userSearch || mentionSearch || '').toLowerCase().trim();
  const filteredUsers = users.filter((u) => {
    const matchesQuery = !normalizedQuery
      || u.name.toLowerCase().includes(normalizedQuery)
      || (u.email || '').toLowerCase().includes(normalizedQuery);
    const matchesRole = roleFilter === 'ALL' || u.role === roleFilter;
    return matchesQuery && matchesRole;
  });

  // Distinct role list derived from the actual users in scope (drives the chips).
  const availableRoles = Array.from(new Set(users.map((u) => u.role).filter(Boolean)));

  // For privileged users in doc threads, show ALL users (with outside visibility indicator)
  // For non-privileged users, only show users in thread visibility
  const inThreadUsers = filteredUsers.filter(u => !u.outside_thread_visibility);
  const outsideThreadUsers = filteredUsers.filter(u => u.outside_thread_visibility === true);

  // Legacy: assigned users for general threads
  const assignedUsers = isPrivileged
    ? filteredUsers  // Show all users to privileged users
    : inThreadUsers; // Only show in-thread users to non-privileged
  const otherUsers = filteredUsers.filter(u => !u.is_assigned);

  // Group mentions are removed — these stay as empty for legacy callsites only.
  const filteredGroups = [];

  return (
    <div className="space-y-2">
      {/* Recipient selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <Popover open={showRecipientSelector} onOpenChange={(o) => { setShowRecipientSelector(o); if (!o) { setUserSearch(''); setRoleFilter('ALL'); } }}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={`${compact ? 'h-7 text-xs' : 'h-8'} gap-1`} data-testid="recipients-button">
              <Users className="w-3 h-3" />
              Recipients
              {selectedMentions.length > 0 && (
                <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs">
                  {selectedMentions.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-3" align="start">
            <div className="space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="recipients-search-input"
                  autoFocus
                />
              </div>

              {/* Role chips */}
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setRoleFilter('ALL')}
                  data-testid="recipients-role-chip-ALL"
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${roleFilter === 'ALL' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  All
                </button>
                {availableRoles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setRoleFilter(role)}
                    data-testid={`recipients-role-chip-${role}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${roleFilter === role ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                  >
                    {getRoleLabel(role)}
                  </button>
                ))}
              </div>

              {/* Users list */}
              <div className="border-t pt-2">
                {assignedUsers.length === 0 ? (
                  <p className="text-xs text-slate-500 italic px-1 py-3">
                    No users match &quot;{userSearch}&quot;{roleFilter !== 'ALL' ? ` in ${getRoleLabel(roleFilter)}` : ''}.
                  </p>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="recipients-user-list">
                    {assignedUsers.map((user) => {
                      const isOutsideThread = user.outside_thread_visibility === true;
                      const isSelected = selectedMentions.includes(user.id);
                      return (
                        <button
                          key={user.id}
                          onClick={() => toggleRecipient(user.id)}
                          data-testid={`recipients-user-${user.id}`}
                          className={`w-full px-2 py-1.5 text-left text-sm rounded flex items-center gap-2 ${
                            isSelected
                              ? isOutsideThread
                                ? 'bg-red-100 text-red-700'
                                : 'bg-blue-100 text-blue-700'
                              : 'hover:bg-slate-50'
                          } ${isOutsideThread ? 'border-l-2 border-red-400' : ''}`}
                        >
                          {isSelected ? (
                            <CheckCircle2 className={`w-4 h-4 ${isOutsideThread ? 'text-red-600' : ''}`} />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-300" />
                          )}
                          <span className="flex-1 truncate">{user.name}</span>
                          {isOutsideThread && (
                            <AlertTriangle className="w-3 h-3 text-red-500" title="Outside thread visibility — will be added" />
                          )}
                          <Badge className={`text-xs ${getRoleColor(user.role)}`}>
                            {getRoleLabel(user.role)}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
        
        {/* Selected recipients tags */}
        {selectedMentions.slice(0, 3).map(userId => {
          const user = users.find(u => u.id === userId);
          const isOutsideThread = user?.outside_thread_visibility === true;
          return user ? (
            <Badge 
              key={userId} 
              className={`gap-1 text-xs ${isOutsideThread ? 'bg-red-100 text-red-700 border border-red-300' : 'bg-amber-100 text-amber-700'}`}
            >
              @{user.name}
              {isOutsideThread && <AlertTriangle className="w-3 h-3" />}
              <X className="w-3 h-3 cursor-pointer" onClick={() => toggleRecipient(userId)} />
            </Badge>
          ) : null;
        })}
        {selectedMentions.length > 3 && (
          <Badge className="bg-slate-100 text-slate-600 text-xs">+{selectedMentions.length - 3} more</Badge>
        )}
      </div>
      
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={compact ? 2 : 3}
          data-testid="comment-input"
        />
        
        {/* Mention dropdown — role chips on top, users below (filtered by chip + @search) */}
        {showMentions && (
          <div
            data-testid="mention-dropdown"
            className="absolute bottom-full left-0 mb-1 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-72 overflow-hidden flex flex-col"
          >
            <div className="p-2 border-b border-slate-100 flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => setRoleFilter('ALL')}
                data-testid="mention-role-chip-ALL"
                className={`text-[11px] px-2 py-0.5 rounded-full border transition ${roleFilter === 'ALL' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
              >
                All
              </button>
              {availableRoles.map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => setRoleFilter(role)}
                  data-testid={`mention-role-chip-${role}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full border transition ${roleFilter === role ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {getRoleLabel(role)}
                </button>
              ))}
            </div>
            <div className="overflow-y-auto p-2">
              {filteredUsers.length === 0 ? (
                <p className="text-xs text-slate-500 italic px-2 py-3">
                  No users match{mentionSearch ? ` "@${mentionSearch}"` : ''}{roleFilter !== 'ALL' ? ` in ${getRoleLabel(roleFilter)}` : ''}.
                </p>
              ) : (
                filteredUsers.slice(0, 30).map((user) => (
                  <button
                    key={user.id}
                    onClick={() => insertMention(user)}
                    data-testid={`mention-user-${user.id}`}
                    className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2 rounded"
                  >
                    <AtSign className="w-4 h-4 text-slate-400" />
                    <span className="font-medium truncate">{user.name}</span>
                    <Badge className={`text-xs ml-auto ${getRoleColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </Badge>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* iter116 — Multi-file onboarding stack. Each selected file gets its own
          gradient card with filename, size, version selector (first card only),
          and an individual remove button. Renders below the textarea so it never
          overlaps the message body. */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2 mt-2" data-testid="attachment-onboarding-card">
          {selectedFiles.length > 1 && (
            <div className="flex items-center justify-between text-[11px] text-slate-600 px-1">
              <span className="font-medium">{selectedFiles.length} files ready to attach</span>
              <button
                onClick={() => { setSelectedFiles([]); setSelectedVersion(null); setContent((c) => (c || '').replace(/(?:^|\n)📎 Attached:[^\n]*/g, '').trimEnd()); }}
                className="text-slate-400 hover:text-red-600 transition-colors"
                title="Remove all attachments"
                data-testid="attachment-clear-all"
              >
                Clear all
              </button>
            </div>
          )}
          {selectedFiles.map((f, idx) => (
            <div
              key={`${f.name}-${f.size}-${idx}`}
              className="flex items-center gap-3 text-xs bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border border-blue-200 px-3 py-2 rounded-lg shadow-sm"
              data-testid={`attachment-file-row-${idx}`}
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-md bg-blue-900 text-white shrink-0">
                <Paperclip className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-sm font-semibold text-blue-950 truncate max-w-[260px]"
                    title={f.name}
                    data-testid={idx === 0 ? "attachment-onboarding-filename" : `attachment-filename-${idx}`}
                  >
                    {f.name}
                  </span>
                  <Badge className="text-[10px] bg-blue-900 text-white border-blue-900 gap-1 px-1.5 py-0">
                    <Paperclip className="w-3 h-3" /> Ready
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-600">
                  <span>{(f.size / 1024).toFixed(1)} KB</span>
                  {idx === 0 && isDocumentThread && (
                    <>
                      <span className="text-slate-300">•</span>
                      <span className="flex items-center gap-1">
                        Linked to
                        <Select
                          value={(selectedVersion ?? defaultVersion)?.toString()}
                          onValueChange={(val) => setSelectedVersion(parseInt(val))}
                        >
                          <SelectTrigger className="h-5 w-20 text-[11px] border-blue-200 bg-white" data-testid="attachment-version-select">
                            <SelectValue placeholder="Version" />
                          </SelectTrigger>
                          <SelectContent>
                            {documentVersions.length > 0 ? (
                              documentVersions.map(v => (
                                <SelectItem key={v.version} value={v.version.toString()}>
                                  v{v.version}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value={latestVersion.toString()}>v{latestVersion}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </span>
                    </>
                  )}
                </div>
                {idx === 0 && autoMentionOnAttach?.name && !autoMentionDismissed && (
                  <p className="mt-1 text-[11px] text-indigo-700 flex items-center gap-1" data-testid="uploader-notify-notice">
                    <Users className="w-3 h-3" />
                    Uploader <span className="font-semibold">@{autoMentionOnAttach.name}</span> will be notified.
                    <button
                      type="button"
                      onClick={() => {
                        // iter161 — Dismiss the uploader auto-mention. Remove
                        // from selected mentions, strip the inline @name prefix
                        // from the textarea, and suppress re-adding on next
                        // attach for the lifetime of this composer instance.
                        // iter167 Issue 16 — Escape regex metacharacters in
                        // the user's name (e.g. "(MH)") so the dismissal
                        // actually strips the full "@Asmaa Alsawadi (MH)"
                        // text from the composer. Without escaping, `(MH)`
                        // was being interpreted as a regex group and the
                        // leftover characters stayed in the textarea.
                        setAutoMentionDismissed(true);
                        setSelectedMentions((prev) => prev.filter((id) => id !== autoMentionOnAttach.id));
                        const escaped = autoMentionOnAttach.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        setContent((prev) => (prev || '').replace(new RegExp(`@${escaped}\\s?`, 'g'), '').trimStart());
                      }}
                      className="ml-1 w-4 h-4 rounded-full bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 flex items-center justify-center transition-colors"
                      title="Don't notify the uploader"
                      aria-label="Dismiss uploader notification"
                      data-testid="uploader-notify-dismiss"
                    >
                      <X className="w-3 h-3" strokeWidth={3} />
                    </button>
                  </p>
                )}
              </div>
              <button
                onClick={() => removeFile(idx)}
                className="text-slate-400 hover:text-red-600 transition-colors shrink-0"
                title="Remove this attachment"
                data-testid={idx === 0 ? "attachment-onboarding-remove" : `attachment-remove-${idx}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* iter164b — Bottom toolbar restructured: Add Attachments + Send live
          together on the left (paperclip then send icon, immediate vicinity).
          Cancel stays on the right. */}
      <div className="flex items-center justify-between gap-2 mt-2">
        <div className="flex items-center gap-2">
          {isDocumentThread && onAttachFile && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                className="hidden"
                data-testid="file-input"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`gap-1 border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 ${compact ? 'h-8 text-xs' : 'h-9 text-xs'}`}
                    title="Attach one or more supporting files to this thread"
                    data-testid="attach-file-btn"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    Add Attachments
                    {selectedFiles.length > 0 && (
                      <Badge className="ml-1 bg-blue-900 text-white text-[10px] px-1 py-0">
                        {selectedFiles.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-3" align="start">
                  <div className="flex items-start gap-2 mb-3">
                    <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Attach one or more supporting files to <b>the latest version</b> (v{latestVersion}).
                      You can change the linked version later via the <span className="font-medium">&quot;Change version&quot;</span> button on each attachment.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="w-full bg-blue-900 hover:bg-blue-950"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="attach-file-select-btn"
                  >
                    <Paperclip className="w-3 h-3 mr-2" />
                    Select File(s)
                  </Button>
                </PopoverContent>
              </Popover>
            </>
          )}
          {/* iter164b — Send sits IMMEDIATELY next to the attachment button. */}
          <Button
            onClick={handleSubmit}
            disabled={(!content.trim() && selectedFiles.length === 0) || submitting}
            size="sm"
            className={`gap-1.5 bg-blue-900 hover:bg-blue-950 ${compact ? 'h-8 px-3 text-xs' : 'h-9 px-4 text-xs'}`}
            data-testid="submit-comment-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send className="w-3.5 h-3.5" />
                Send
              </>
            )}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button
              onClick={onCancel}
              variant="ghost"
              size="sm"
              className={`text-slate-500 hover:text-slate-700 ${compact ? 'h-8 text-xs' : 'h-9'}`}
              data-testid="cancel-comment-btn"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Cancel
            </Button>
          )}
        </div>
      </div>
      
      {!compact && (
        <p className="text-xs text-slate-400">
          Type @ to mention users or groups. Press Ctrl+Enter to send.{isDocumentThread && onAttachFile ? ' Click 📎 to attach files.' : ''}
        </p>
      )}
      
      {/* Warning Modal for adding users outside thread visibility */}
      <Dialog open={showVisibilityWarning} onOpenChange={setShowVisibilityWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Add User to Thread Visibility?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {pendingSubmit?.outsideUsers.map(user => (
              <div key={user.id} className="flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700 border border-red-300">
                  {user.name} ({getRoleLabel(user.role)})
                </Badge>
                <span className="text-sm text-slate-500">is not in this thread</span>
              </div>
            ))}
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              <p className="text-sm text-amber-800 font-medium">Mentioning them will:</p>
              <ul className="text-sm text-amber-700 space-y-1 ml-4 list-disc">
                <li>Add them to thread visibility</li>
                <li>Allow them to see entire thread history</li>
                <li>Allow them to see all documents in this thread</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelVisibilityExpansion}>
              Cancel
            </Button>
            <Button onClick={handleConfirmVisibilityExpansion} className="bg-amber-600 hover:bg-amber-700">
              Confirm & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empty recipients confirmation dialog */}
      <Dialog open={showEmptyRecipientsWarning} onOpenChange={setShowEmptyRecipientsWarning}>
        <DialogContent data-testid="empty-recipients-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Send without notifying anyone?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">
            Your message has no <strong>@mention</strong> and no selected <strong>recipient</strong>.
            Without these, the people who should see this message <em>will not receive an immediate
            notification</em> — they&apos;ll only see it when they next open this discussion.
          </p>
          <p className="text-sm text-slate-700">
            For best results, add an <code>@mention</code> in the message body or use the
            <strong> Recipients</strong> button to pick people to notify.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowEmptyRecipientsWarning(false); setPendingEmptySubmit(null); }}
              data-testid="empty-recipients-cancel"
            >
              Go Back &amp; Add Recipients
            </Button>
            <Button
              onClick={() => {
                const c = pendingEmptySubmit?.content ?? content;
                setShowEmptyRecipientsWarning(false);
                setPendingEmptySubmit(null);
                executeSubmit(c, [], []);
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="empty-recipients-confirm"
            >
              Send Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Thread Component - A card containing root message + replies
function ThreadCard({ thread, onResolve, onResolveThread, onDelete, onEdit, onReply, onMarkSeen, onStatusChange, onAssignStatusReviewers, onUploadNewVersion, uploadingNewVersion = false, onDownloadAttachment, onPreviewDoc, onAttachFile, onEditAttachmentVersion, currentUserId, mentionData, canManageStatus, assignedTeam, documentVersions = [], isOverrideRole = false, requestId, fetchThreadMentionData, readOnly = false, highlightedThreadId = null, userHasApproved = false, pulseStatusHint = false }) {
  const { isCompanyRep, isAdmin, isCRD, isChairman, isDirector, isExecDirector } = useAuth();
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!thread.is_thread_resolved);
  const [showAssignPopover, setShowAssignPopover] = useState(false);
  const [uploadVersionPopoverOpen, setUploadVersionPopoverOpen] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState(thread.document_status_assignees || []);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [prefilledReplyContent, setPrefilledReplyContent] = useState('');
  const [threadMentionData, setThreadMentionData] = useState(null);
  const [loadingMentionData, setLoadingMentionData] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  
  // Status labels for display
  const statusLabels = {
    'PENDING_REVIEW': 'Pending Review',
    'CHANGES_REQUESTED': 'Changes Requested',
    'APPROVED': 'Approved'
  };
  
  // Mark thread as seen when user views it
  useEffect(() => {
    const visibleTo = thread.visible_to || [];
    const seenBy = thread.seen_by || [];
    const alreadySeen = seenBy.some(s => s.user_id === currentUserId);
    
    // Mark as seen if user is in visible_to OR has override role (Admin/Chairman/CRD/Coordinator)
    const canSee = visibleTo.includes(currentUserId) || isOverrideRole;
    
    if (canSee && !alreadySeen && thread.user_id !== currentUserId) {
      onMarkSeen(thread.id);
    }
  }, [thread.id, thread.visible_to, thread.seen_by, thread.user_id, currentUserId, onMarkSeen, isOverrideRole]);
  
  // Fetch thread-specific mention data when opening reply for document threads
  const handleOpenReply = async () => {
    setShowReplyInput(true);
    
    // For document threads, fetch thread-specific mention data
    if (thread.is_document_thread && fetchThreadMentionData) {
      setLoadingMentionData(true);
      try {
        const data = await fetchThreadMentionData(thread.id);
        setThreadMentionData(data);
      } catch (error) {
        console.error('Failed to fetch thread mention data:', error);
      } finally {
        setLoadingMentionData(false);
      }
    }
  };
  
  const handleReply = async (content, mentions, mentionGroups) => {
    // If there's a pending status change, apply it along with the reply
    if (pendingStatusChange && onStatusChange && thread.document_id) {
      await onStatusChange(thread.document_id, pendingStatusChange, content);
      setPendingStatusChange(null);
    } else {
      onReply(thread.id, content, mentions, mentionGroups);
    }
    setShowReplyInput(false);
    setPrefilledReplyContent('');
    setThreadMentionData(null); // Reset thread-specific data
  };

  const handleStatusChange = async (newStatus) => {
    if (onStatusChange && thread.document_id) {
      setStatusChangeLoading(true);
      try {
        // Directly update status without creating a message
        await onStatusChange(thread.document_id, newStatus);
      } finally {
        setStatusChangeLoading(false);
      }
    }
  };

  const handleCancelReply = () => {
    setShowReplyInput(false);
    setPendingStatusChange(null);
    setPrefilledReplyContent('');
    setThreadMentionData(null); // Reset thread-specific data
  };

  const handleAssignStatusReviewers = async () => {
    if (onAssignStatusReviewers && thread.document_id) {
      setAssigneesLoading(true);
      try {
        await onAssignStatusReviewers(thread.document_id, selectedAssignees);
        setShowAssignPopover(false);
      } finally {
        setAssigneesLoading(false);
      }
    }
  };

  // iter149 — exclude legacy ClinicalFlow Agent / SYSTEM auto-banners from the
  // reply count so the chip on each thread reflects only real conversation.
  // iter158 — also drop legacy assignment_change rows that lack assignees_meta
  // (they are hidden from render for Company viewers — keep the count honest).
  const replyCount = (thread.replies || []).filter((r) => {
    if (r.user_role === 'COORDINATOR_AGENT' || r.user_role === 'SYSTEM_AGENT') return false;
    if (r.is_system_message && !r.is_signed_marker) return false;
    if (isCompanyRep() && r.is_assignment_change && (!Array.isArray(r.assignees_meta) || r.assignees_meta.length === 0)) return false;
    return true;
  }).length;
  const isResolved = thread.is_thread_resolved;
  const isDocumentThread = thread.is_document_thread;
  
  // Use thread-specific mention data for document threads, otherwise use general mentionData
  const effectiveMentionData = isDocumentThread && threadMentionData ? threadMentionData : mentionData;

  // Check if current user can change status
  // Lock: if user has already approved the request, they cannot change doc statuses anymore
  const canChangeStatus = (canManageStatus || (thread.document_status_assignees || []).includes(currentUserId)) && !userHasApproved;

  // iter115 — auto-mention payload passed to CommentInput. When the current
  // user is an eligible reviewer (admin / CRD / chairman / director / exec director
  // / status-assignee) AND not the original uploader, we auto-prepend an @mention
  // of the uploader the moment they attach a file. Mirrors the backend safety net.
  const isEligibleReviewer = isAdmin?.() || isCRD?.() || isChairman?.()
    || isDirector?.() || isExecDirector?.()
    || (thread.document_status_assignees || []).includes(currentUserId);
  const autoMentionOnAttach = (
    isDocumentThread
    && isEligibleReviewer
    && thread.user_id
    && thread.user_id !== currentUserId
    && thread.user_name
  ) ? { id: thread.user_id, name: thread.user_name } : null;
  
  // Check if this is an access request thread
  const isAccessRequest = thread.is_access_request;

  // Highlight from doc-gate navigation (red ring + pulse)
  const isHighlighted = highlightedThreadId === thread.id;

  return (
    <Card 
      className={`${
        isResolved ? 'bg-slate-50 border-slate-300' : 
        isAccessRequest ? 'border-amber-300 bg-amber-50/50' :
        isDocumentThread ? 'border-blue-200 bg-blue-50/30' : 
        'border-slate-200'
      } shadow-sm transition-all duration-300 ${
        isHighlighted ? 'ring-2 ring-red-500 ring-offset-2 animate-pulse' : ''
      }`} 
      data-testid={`thread-${thread.id}`}
      data-comment-id={thread.id}
      data-thread-id={thread.id}
    >
      {/* Access Request Thread Header */}
      {isAccessRequest && (
        <div className="px-4 py-2 border-b border-amber-300 bg-amber-100">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="font-medium text-amber-900">Access Request</span>
            {thread.access_request_doc_type && (
              <Badge variant="outline" className="text-xs bg-amber-200 text-amber-800 border-amber-400">
                {thread.access_request_doc_type} Thread
              </Badge>
            )}
          </div>
        </div>
      )}
      
      {/* Document Thread Header — iter108: locked layout
            Row 1 (always): [icon][filename][doc_type chip]  ←→  [status badge][assign btn][upload btn]
            Row 2 (meta):   "Assigned to: …"      |   "Visible to: …"
            Row 3 (action): Preview <filename>
          This guarantees the same positions across every doc thread, regardless of
          whether visible_to / assignees / status_select are present.                       */}
      {isDocumentThread && (
        <div className="px-4 py-2.5 border-b border-blue-200 bg-blue-50">
          {/* Row 1 — iter122: doc TYPE is the prominent header (e.g., "Protocol"),
              not the filename. The filename is still available on the Preview button
              in row 3 and inside the seeded message body. */}
          <div className="flex items-start justify-between gap-3 min-w-0">
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-5 h-5 text-blue-700 shrink-0" />
                <h3
                  className="text-lg font-semibold text-blue-900 truncate leading-tight"
                  title={thread.document_doc_type || thread.document_filename || 'Document'}
                  data-testid="thread-header-doctype"
                >
                  {thread.document_doc_type || 'Document'}
                </h3>
              </div>
              {/* iter137 — status chip now sits BELOW the doc type (per user request)
                  so the eye reads "what kind of doc → what state is it in" top-to-bottom. */}
              {thread.document_status && (
                <div className="pl-7" data-testid={`thread-status-chip-row-${thread.id}`}>
                  <DocumentStatusBadge status={thread.document_status} />
                </div>
              )}
            </div>

            {/* Right-aligned action cluster — always rendered as the SAME flex-row
                so chips/buttons stay in the same place across threads. */}
            <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
              {canChangeStatus && !readOnly && canManageStatus && (
                <div className="relative">
                  <Select value={thread.document_status} onValueChange={handleStatusChange} disabled={statusChangeLoading}>
                    <SelectTrigger
                      className={`h-7 w-40 text-xs ${pulseStatusHint ? 'ring-2 ring-amber-400 ring-offset-1 animate-pulse border-amber-400' : ''}`}
                      data-testid={`thread-status-select-${thread.id}`}
                    >
                      {statusChangeLoading ? (
                        <span className="flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Updating...
                        </span>
                      ) : (
                        <SelectValue placeholder="Change status" />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING_REVIEW">🔵 Pending Review</SelectItem>
                      <SelectItem value="CHANGES_REQUESTED">🟡 Changes Requested</SelectItem>
                      <SelectItem value="APPROVED">🟢 Approved</SelectItem>
                    </SelectContent>
                  </Select>
                  {/* iter116 — one-shot visual hint that appears for ~6s after an
                      attachment auto-flips the status. Arrow points to the Select. */}
                  {pulseStatusHint && (
                    <div
                      className="absolute -top-7 right-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold shadow-lg animate-bounce whitespace-nowrap"
                      data-testid={`thread-status-pulse-hint-${thread.id}`}
                    >
                      Click to change
                      <span className="text-base leading-none">↓</span>
                    </div>
                  )}
                </div>
              )}
              {/* iter136 — Non-privileged assignees (PI / Investigator / Reviewer) no longer
                  see the full status dropdown. Instead they get a single "Approve" button
                  that ticks them off the per-assignee ledger. To request changes, they
                  upload an attachment (which auto-flips the doc to Changes Requested). */}
              {canChangeStatus && !readOnly && !canManageStatus
                && (thread.document_status_assignees || []).includes(currentUserId)
                && !(thread.document_status_approved_by || []).includes(currentUserId)
                && thread.document_status !== 'APPROVED' && (
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                  onClick={() => handleStatusChange('APPROVED')}
                  disabled={statusChangeLoading}
                  data-testid={`thread-approve-btn-${thread.id}`}
                  title="Approve this doc thread from your side. To request changes, attach a file below."
                >
                  {statusChangeLoading ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Approving...</>
                  ) : (
                    <><CheckCircle2 className="w-3 h-3" /> Approve</>
                  )}
                </Button>
              )}
              {userHasApproved && !readOnly && (canManageStatus || (thread.document_status_assignees || []).includes(currentUserId)) && (
                <Badge variant="outline" className="h-7 text-xs gap-1 border-slate-300 bg-slate-100 text-slate-600" data-testid={`thread-status-locked-${thread.id}`}>
                  <Lock className="w-3 h-3" /> Locked (you approved)
                </Badge>
              )}
              {/* iter135 — "Approved from your side" badge for PI/Investigator/Reviewer who
                  have personally approved a multi-assignee doc thread that is still pending
                  on other peers. Lets them visually confirm their tick was recorded without
                  conflating it with the overall doc status. */}
              {!readOnly
                && (thread.document_status_assignees || []).includes(currentUserId)
                && (thread.document_status_approved_by || []).includes(currentUserId)
                && thread.document_status !== 'APPROVED' && (
                <Badge
                  variant="outline"
                  className="h-7 text-xs gap-1 border-emerald-300 bg-emerald-50 text-emerald-700"
                  data-testid={`thread-status-approved-mine-${thread.id}`}
                  title="You've approved this doc thread from your side. It stays Pending Review until the other assignees approve too."
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Approved (from your side)
                </Badge>
              )}
              {canManageStatus && !readOnly && (
                <Popover open={showAssignPopover} onOpenChange={setShowAssignPopover}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                      <UserPlus className="w-3 h-3" /> Assign to:
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3">
                    <div className="text-sm font-medium mb-2">Assign Document Reviewers</div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {/* iter118 — exclude the current user from the assign-to list:
                          no one can assign a document review to themselves. */}
                      {(assignedTeam || []).filter(m => m.id !== currentUserId).length === 0 && (
                        <div className="text-xs text-slate-500 italic">No other team members available to assign.</div>
                      )}
                      {(assignedTeam || []).filter(m => m.id !== currentUserId).map(member => (
                        <label key={member.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedAssignees.includes(member.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAssignees([...selectedAssignees, member.id]);
                              } else {
                                setSelectedAssignees(selectedAssignees.filter(id => id !== member.id));
                              }
                            }}
                            className="rounded"
                            disabled={assigneesLoading}
                          />
                          {member.name}
                        </label>
                      ))}
                    </div>
                    <Button size="sm" className="w-full mt-2" onClick={handleAssignStatusReviewers} disabled={assigneesLoading}>
                      {assigneesLoading ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...
                        </>
                      ) : 'Save Assignees'}
                    </Button>
                  </PopoverContent>
                </Popover>
              )}
              {/* Upload New Version button - hidden when readOnly (iter109: clearer cloud icon
                  + helper popover so users understand this is for versioning, not attachments) */}
              {onUploadNewVersion && !readOnly && (
                <Popover open={uploadVersionPopoverOpen} onOpenChange={setUploadVersionPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1 border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                      disabled={uploadingNewVersion}
                      data-testid={`thread-upload-new-version-btn-${thread.id}`}
                    >
                      {uploadingNewVersion
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <CloudUpload className="w-3.5 h-3.5" />}
                      {uploadingNewVersion ? 'Uploading…' : 'Upload New Version'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-3" align="end">
                    <div className="flex items-start gap-2 mb-3">
                      <Info className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Use this to upload a <b>new version of this document only</b> — not for adding
                        supporting attachments. To attach a supporting file, open <span className="font-medium">Reply</span>
                        and click the <span className="font-medium">Attach file</span> button instead.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="w-full bg-indigo-700 hover:bg-indigo-800"
                      onClick={() => {
                        // iter110 — close the popover first so the GenerateBundleDialog
                        // (when bundle-chain) can take focus without being blocked.
                        setUploadVersionPopoverOpen(false);
                        // Defer the parent callback to the next tick so the popover
                        // close animation completes before the dialog mounts.
                        setTimeout(() => onUploadNewVersion(thread), 0);
                      }}
                      disabled={uploadingNewVersion}
                      data-testid={`thread-upload-new-version-confirm-${thread.id}`}
                    >
                      <CloudUpload className="w-3.5 h-3.5 mr-2" />
                      Choose new version file…
                    </Button>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {/* Row 2 — Meta row (Assigned to + Visible to). Always allocates the row when
              either piece exists, never floats inside row-1. Keeps a constant baseline.
              iter158 — For Company / Company Rep show a role-masked summary:
              Coordinator names + role explicitly + counts for other roles. */}
          {((thread.document_reviewer_names && thread.document_reviewer_names.length > 0)
              || (thread.visible_to && thread.visible_to.length > 0 && !isCompanyRep())) && (
            <div className="mt-1.5 flex items-center gap-4 flex-wrap text-xs">
              {thread.document_reviewer_names && thread.document_reviewer_names.length > 0 && (
                <div className="flex items-center gap-1.5 text-blue-700" data-testid="thread-assigned-to-strip">
                  <Users className="w-3 h-3" />
                  <span>
                    Assigned to: {(() => {
                      const meta = Array.isArray(thread.document_reviewer_meta) ? thread.document_reviewer_meta : [];
                      // Non-company users keep full names.
                      if (!isCompanyRep() || meta.length === 0) {
                        return thread.document_reviewer_names.join(', ');
                      }
                      const byRole = {};
                      for (const r of meta) {
                        const k = r.role || 'UNKNOWN';
                        (byRole[k] ||= []).push(r);
                      }
                      const roleLabel = (r) => (r || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                      const segs = [];
                      if (byRole['COORDINATOR']) {
                        segs.push(`Coordinator ${byRole['COORDINATOR'].map(c => c.name).join(', ')}`);
                        delete byRole['COORDINATOR'];
                      }
                      Object.keys(byRole).sort().forEach(r => {
                        const n = byRole[r].length;
                        const lbl = roleLabel(r);
                        segs.push(`${n} ${n === 1 ? lbl : lbl + 's'}`);
                      });
                      return segs.join(', ');
                    })()}
                  </span>
                </div>
              )}
              {thread.visible_to && thread.visible_to.length > 0 && !isCompanyRep() && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-2 py-0.5 rounded transition-colors">
                      <Eye className="w-3 h-3" />
                      <span>Visible to:</span>
                      {thread.visible_to_names ? (
                        <span className="text-slate-600">
                          {thread.visible_to_names.slice(0, 3).map(u => u.name).join(', ')}
                          {thread.visible_to_names.length > 3 && (
                            <span className="text-blue-600 ml-1">+{thread.visible_to_names.length - 3} more</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-600">{thread.visible_to.length} users</span>
                      )}
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700 pb-2 border-b">
                        <Eye className="w-4 h-4" />
                        <span>Users with access ({thread.visible_to_names?.length || thread.visible_to.length})</span>
                      </div>
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {thread.visible_to_names ? (
                          thread.visible_to_names.map((u, idx) => (
                            <div key={idx} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50">
                              <span className="text-sm text-slate-700">{u.name}</span>
                              <Badge className={`text-xs ${getRoleColor(u.role)}`}>
                                {getRoleLabel(u.role)}
                              </Badge>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500 py-2">{thread.visible_to.length} users have access</p>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}
        </div>
      )}

      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* iter138 — same gradient AI-agent avatar treatment as the reply renderer
                so a SYSTEM-authored top-level thread (rare but possible) is also clearly
                marked as autonomously emitted. */}
            {thread.user_role === 'COORDINATOR_AGENT' ? (
              <div
                className="relative w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center text-white shadow-md ring-2 ring-purple-300/60"
                data-testid={`thread-${thread.id}-agent-avatar`}
                title="ClinicalFlow Agent"
              >
                <Sparkles className="w-4 h-4" />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white animate-pulse" />
              </div>
            ) : (
              <div className={`w-8 h-8 rounded-full ${isDocumentThread ? 'bg-blue-600' : 'bg-blue-900'} flex items-center justify-center text-white text-sm font-medium`}>
                {isDocumentThread ? <FileText className="w-4 h-4" /> : thread.user_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className={`font-medium ${thread.user_role === 'COORDINATOR_AGENT' ? 'bg-gradient-to-r from-indigo-700 via-purple-700 to-pink-700 bg-clip-text text-transparent' : 'text-slate-900'}`}>
                  {thread.user_name}
                </span>
                <Badge className={`text-xs ${getRoleColor(thread.user_role)} ${thread.user_role === 'COORDINATOR_AGENT' ? 'gap-1' : ''}`}>
                  {thread.user_role === 'COORDINATOR_AGENT' && <Sparkles className="w-2.5 h-2.5" />}
                  {getRoleLabel(thread.user_role)}
                </Badge>
                {isResolved && (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Thread Resolved</Badge>
                )}
              </div>
              <span className="text-xs text-slate-500">{formatDateTime(thread.created_at)}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <SeenIndicator comment={thread} currentUserId={currentUserId} hideForCompany={isCompanyRep()} />
            
            {replyCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-7 text-xs gap-1"
              >
                {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </Button>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowReplyInput(!showReplyInput)}>
                  <Reply className="w-4 h-4 mr-2" /> Reply
                </DropdownMenuItem>
                {!isDocumentThread && (
                  <>
                    <DropdownMenuItem onClick={() => onResolveThread(thread.id, !isResolved)}>
                      {isResolved ? (
                        <><Circle className="w-4 h-4 mr-2" /> Unresolve Thread</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 mr-2" /> Resolve Thread</>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onResolve(thread.id, !thread.is_resolved)}>
                      {thread.is_resolved ? (
                        <><Circle className="w-4 h-4 mr-2" /> Unresolve Message</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 mr-2" /> Resolve Message</>
                      )}
                    </DropdownMenuItem>
                  </>
                )}
                {(thread.user_id === currentUserId || isAdmin()) && (
                  <DropdownMenuItem onClick={() => onDelete(thread.id)} className="text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete{isAdmin() && thread.user_id !== currentUserId ? ' (Admin)' : ''}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className={`px-4 pb-4 pt-0 ${isResolved ? 'opacity-70' : ''}`}>
        {/* Thread root message content */}
        <div className={`mb-3 ${thread.is_resolved && !isResolved ? 'line-through opacity-60' : ''}`}>
          <p className="text-slate-700 whitespace-pre-wrap">
            {(() => {
              let result = thread.content;
              if (thread.mention_names && thread.mention_names.length > 0) {
                thread.mention_names.forEach(name => {
                  const regex = new RegExp(`@${name}`, 'gi');
                  result = result.replace(regex, `<span class="bg-amber-100 text-amber-800 px-1 rounded">@${name}</span>`);
                });
              }
              const groupMentions = ['@all', '@internal', '@Reviewers', '@PIs', '@Consultants'];
              groupMentions.forEach(group => {
                const regex = new RegExp(group, 'gi');
                result = result.replace(regex, `<span class="bg-blue-100 text-blue-800 px-1 rounded">${group}</span>`);
              });
              return <span dangerouslySetInnerHTML={{ __html: result }} />;
            })()}
          </p>

          {/* Recipients (picked via @ or the Recipients button) with their role chip */}
          {thread.mention_users && thread.mention_users.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`message-${thread.id}-recipients`}>
              <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">To:</span>
              {thread.mention_users.map((mu) => (
                <span
                  key={mu.id}
                  className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-full pl-2 pr-1 py-0.5 text-xs text-slate-700"
                  data-testid={`message-recipient-${mu.id}`}
                >
                  {mu.name}
                  <Badge className={`text-[10px] px-1.5 py-0 ${getRoleColor(mu.role)}`}>
                    {getRoleLabel(mu.role)}
                  </Badge>
                </span>
              ))}
            </div>
          )}
          
          {/* Download button for the main document in document threads */}
          {isDocumentThread && thread.document_id && thread.document_filename && (
            <div className="mt-3 min-w-0 flex items-center gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-auto min-h-8 text-xs gap-2 max-w-full whitespace-normal py-1.5 text-left"
                onClick={() => onPreviewDoc && onPreviewDoc(thread.document_id)}
                title={thread.document_filename}
                data-testid={`thread-preview-doc-${thread.document_id}`}
              >
                <Eye className="w-4 h-4 shrink-0" />
                <span className="truncate flex-1 min-w-0">Preview {thread.document_filename}</span>
              </Button>
              {/* iter117 — "Version N" chip beside the doc preview button so reviewers
                  always see which version this thread is rooted on. */}
              {thread.document_version && (
                <Badge
                  className="text-[10px] bg-indigo-50 text-indigo-800 border border-indigo-200 gap-1"
                  data-testid={`thread-version-chip-${thread.document_id}`}
                >
                  <Layers className="w-3 h-3" />
                  Version {thread.document_version}
                </Badge>
              )}
            </div>
          )}
          
          {/* Visibility display - hide for document threads and Company Reps */}
          {!isDocumentThread && !isCompanyRep() && (thread.visibility_display || (thread.mention_groups && thread.mention_groups.length > 0)) && (
            <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
              <Users className="w-3 h-3" />
              <span>Visible to: {thread.visibility_display || thread.mention_groups?.join(', ')}</span>
            </div>
          )}
        </div>
        
        {/* Replies */}
        {isExpanded && thread.replies && thread.replies.length > 0 && (
          <div className="space-y-2 pl-4 border-l-2 border-slate-200 mb-3">
            {(() => {
              // iter149 — ClinicalFlow Agent removed. Hide any legacy/in-flight
              // COORDINATOR_AGENT or SYSTEM_AGENT auto-banners from the render so
              // the discussion looks clean across both new and historical requests.
              // Signed-markers and version-markers are NOT agent messages — they
              // pass through untouched.
              //
              // iter155 — Phase E role-masking for Company / Company Rep viewers:
              //   • partial-approval markers (is_partial_approval=True) — HIDDEN
              //     for Company/Rep. They only see the conclusive approval.
              //   • assignment_change comments (is_assignment_change=True) — HIDDEN
              //     for Company/Rep UNLESS one of the assignees is a Coordinator
              //     (assignment_roles_list includes "COORDINATOR"). Coordinator
              //     assignment is the only one Company/Rep needs to see.
              const isCompanyView = isCompanyRep();
              const visibleReplies = thread.replies.filter(r => {
                if (r.is_signed_marker) return true;
                if (r.user_role === 'COORDINATOR_AGENT' || r.user_role === 'SYSTEM_AGENT') return false;
                if (r.is_system_message) return false;
                // iter157 — "Approved by: …" markers VISIBLE to Company so they
                // can see each approval and @-mention the approver.
                // iter158 — "Assigned to: …" comments are visible to Company
                // BUT only when they carry assignees_meta (the role-masked
                // render needs that data). Legacy/historical rows without
                // meta are hidden to avoid leaking full names.
                if (isCompanyView && r.is_assignment_change) {
                  if (!Array.isArray(r.assignees_meta) || r.assignees_meta.length === 0) return false;
                }
                return true;
              });
              // iter111 — pre-compute the highest version_marker so we can paint a
              // "Latest version" chip on the most recent uploaded version row.
              const versionMarkers = visibleReplies.filter(r => r.is_version_marker && r.attachment_related_version);
              const latestVersionMessageId = versionMarkers.length > 0
                ? versionMarkers.reduce((a, b) =>
                    (a.attachment_related_version || 0) >= (b.attachment_related_version || 0) ? a : b
                  ).id
                : null;
              // iter148j (legacy) — agent partial-approval collapsing kept as a
              // no-op now that the agent is gone; visibleReplies has already
              // filtered them out, so the grouping below simply renders singles.
              const isAgentMsg = (m) => (m.user_role === 'COORDINATOR_AGENT' || m.user_role === 'SYSTEM_AGENT') && !m.is_signed_marker;
              const isPartialAgentMsg = (m) => {
                if (!isAgentMsg(m)) return false;
                const c = (m.content || '').toLowerCase();
                // Final/conclusive markers — keep these expanded.
                if (c.includes('fully approved')) return false;
                if (c.includes('please proceed to the next step')) return false;
                // Everything else from the agent (partial counts, override notes about
                // pending reviewers, generic progress) collapses.
                return true;
              };
              const grouped = [];
              let buf = [];
              const flushBuf = () => {
                if (buf.length === 0) return;
                if (buf.length === 1) grouped.push({ kind: 'single', reply: buf[0] });
                else grouped.push({ kind: 'agent-group', replies: [...buf], id: `grp-${buf[0].id}` });
                buf = [];
              };
              for (const reply of visibleReplies) {
                if (isPartialAgentMsg(reply)) buf.push(reply);
                else { flushBuf(); grouped.push({ kind: 'single', reply }); }
              }
              flushBuf();
              return grouped.map(g => {
                if (g.kind === 'agent-group') {
                  return (
                    <AgentMessagesGroup
                      key={g.id}
                      replies={g.replies}
                      onMarkSeen={onMarkSeen}
                      onPreviewDoc={onPreviewDoc}
                      currentUserId={currentUserId}
                      isThreadResolved={isResolved}
                      documentVersions={documentVersions}
                      isOverrideRole={isOverrideRole}
                      hideSeenForCompany={isCompanyRep()}
                    />
                  );
                }
                const reply = g.reply;
                return (
                reply.is_signed_marker ? (
                  <SignedMarkerBanner key={reply.id} reply={reply} onPreviewDoc={onPreviewDoc} />
                ) : (
                  <MessageItem
                    key={reply.id}
                    message={reply}
                    onResolve={onResolve}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onMarkSeen={onMarkSeen}
                    onDownloadAttachment={onDownloadAttachment}
                    onPreviewDoc={onPreviewDoc}
                    onEditAttachmentVersion={onEditAttachmentVersion}
                    currentUserId={currentUserId}
                    isThreadResolved={isResolved}
                    documentVersions={documentVersions}
                    isOverrideRole={isOverrideRole}
                    hideSeenForCompany={isCompanyRep()}
                    isLatestVersion={reply.id === latestVersionMessageId}
                  />
                ));
              });
            })()}
          </div>
        )}
        
        {/* Reply input - inside the thread */}
        {showReplyInput && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            {/* Show loading indicator while fetching thread-specific data */}
            {loadingMentionData && (
              <p className="text-xs text-slate-400 mb-2">Loading mention options...</p>
            )}
            
            {/* Show restriction message for non-privileged users in document threads */}
            {isDocumentThread && effectiveMentionData && !effectiveMentionData.is_privileged && effectiveMentionData.restriction_message && (
              <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mb-2">
                <AlertTriangle className="w-3 h-3" />
                <span>{effectiveMentionData.restriction_message}</span>
              </div>
            )}
            
            <p className="text-xs text-slate-500 mb-2">
              {pendingStatusChange ? 'Edit the status change message before sending:' : 'Reply to this thread:'}
            </p>
            <CommentInput
              onSubmit={handleReply}
              onAttachFile={isDocumentThread ? (content, mentions, groups, file, version, notifyUploader) => onAttachFile(content, mentions, groups, file, version, notifyUploader) : null}
              mentionData={effectiveMentionData}
              placeholder="Write a reply..."
              compact={true}
              isDocumentThread={isDocumentThread}
              documentVersions={documentVersions}
              latestVersion={thread.document_version || 1}
              prefilledContent={prefilledReplyContent}
              onCancel={pendingStatusChange ? handleCancelReply : null}
              autoMentionOnAttach={autoMentionOnAttach}
            />
          </div>
        )}
        
        {/* Quick reply button when input is hidden - hidden when readOnly */}
        {!showReplyInput && !readOnly && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleOpenReply}
            className="text-xs text-slate-500 h-7 mt-2"
          >
            <Reply className="w-3 h-3 mr-1" /> Reply to thread
          </Button>
        )}
        
        {/* Thread resolution info */}
        {isResolved && thread.thread_resolved_by_name && (
          <p className="text-xs text-emerald-600 mt-2">
            Thread resolved by {thread.thread_resolved_by_name} on {formatDateTime(thread.thread_resolved_at)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Main Discussion Thread Component
export function DiscussionThread({ requestId, requestType = null, documentId = null, title = "Discussion", highlightedCommentId = null, highlightedThreadId = null, highlightStyle = null, assignedTeam = [], onDocumentsChanged = null, onPreviewDoc = null, onOpenBundleNewVersion = null, readOnly = false, userHasApproved = false, refreshKey = 0 }) {
  const { user, isCoordinator, isCRD, isChairman, isAdmin, isDirector, isExecDirector, isFinance } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [comments, setComments] = useState([]);
  const [mentionData, setMentionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  // iter117 — Thread-type filter: 'all' | 'document' | 'general'
  const [threadTypeFilter, setThreadTypeFilter] = useState('all');
  // iter116 — when an attachment auto-flips a thread's status, this holds that
  // thread's id for a few seconds so the ThreadCard can pulse an arrow on the
  // status button (visual hint to "change it from the header").
  const [pulseStatusHintForThreadId, setPulseStatusHintForThreadId] = useState(null);
  const highlightedRef = useRef(null);

  // Check if user can manage document status / assign reviewers.
  // FT exception: coordinators (incl. the request creator) have NO blanket rights on FT doc
  // threads — they may only change a thread's status when assigned to it. The FO (Finance)
  // CAN manage FT doc threads.
  const canManageStatus = (isCoordinator() && requestType !== 'FT')
    || isCRD() || isChairman() || isAdmin() || isDirector() || isExecDirector()
    || (isFinance() && requestType === 'FT');
  // Seen-marking override keeps the old role list (coordinators still see all threads)
  const isOverrideViewer = isCoordinator() || isCRD() || isChairman() || isAdmin() || isDirector() || isExecDirector();

  useEffect(() => {
    fetchComments();
    fetchMentionData();
  }, [requestId, documentId, filter, refreshKey]);

  // Scroll to highlighted comment when data loads — ONE-SHOT per (id+style)
  const highlightAppliedRef = useRef('');
  useEffect(() => {
    if (!highlightedCommentId || loading || comments.length === 0) return;
    const key = `${highlightedCommentId}|${highlightStyle || ''}`;
    if (highlightAppliedRef.current === key) return; // already applied for this nav
    highlightAppliedRef.current = key;
    setTimeout(() => {
      const element = document.querySelector(`[data-comment-id="${highlightedCommentId}"]`);
      if (!element) return;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Red ring for nudge notifications, soft blue for doc-preview "Go to thread", amber otherwise
      const ringClasses = highlightStyle === 'nudge'
        ? ['ring-4', 'ring-red-500', 'ring-offset-2', 'bg-red-50']
        : highlightStyle === 'doc-preview'
          ? ['ring-2', 'ring-blue-400', 'ring-offset-2', 'bg-blue-50/60']
          : ['ring-2', 'ring-amber-400', 'ring-offset-2'];
      element.classList.add(...ringClasses);
      // ⚠ Clear URL highlight params IMMEDIATELY (not after the timer) so tab-switches
      // and refreshes during the visual window don't re-apply on remount.
      // We use react-router's setSearchParams so the parent's React state syncs (just
      // calling window.history.replaceState leaves stale state in useSearchParams,
      // which would re-trigger this useEffect when the component remounts on tab-switch).
      try {
        if (searchParams.get('highlight') === highlightStyle
            && searchParams.get('comment') === highlightedCommentId) {
          const next = new URLSearchParams(searchParams);
          next.delete('highlight');
          next.delete('comment');
          setSearchParams(next, { replace: true });
        }
      } catch { /* noop */ }
      const duration = (highlightStyle === 'nudge' || highlightStyle === 'doc-preview') ? 6000 : 3000;
      setTimeout(() => {
        element.classList.remove(...ringClasses);
        // Reset the applied-ref so a *next* Go-to-Discussion click can re-trigger,
        // even if the new click happens to target a different doc thread.
        highlightAppliedRef.current = '';
      }, duration);
    }, 100);
  }, [highlightedCommentId, highlightStyle, loading, comments]);

  const fetchComments = async () => {
    try {
      const params = {};
      if (documentId) params.document_id = documentId;
      if (filter === 'unresolved') params.resolved = false;
      
      const res = await commentsApi.getAll(requestId, params);
      setComments(res.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMentionData = async () => {
    try {
      const res = await commentsApi.getMentionUsers(requestId);
      setMentionData(res.data);
    } catch (error) {
      console.error('Failed to fetch mention users:', error);
    }
  };

  // Fetch thread-specific mention data for document threads
  const fetchThreadMentionData = async (threadId) => {
    try {
      const res = await commentsApi.getMentionUsers(requestId, threadId);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch thread mention data:', error);
      return mentionData; // Fall back to general mention data
    }
  };

  const handleSubmitNewThread = async (content, mentions, mentionGroups) => {
    try {
      const data = {
        content,
        document_id: documentId,
        parent_id: null,
        mentions,
        mention_groups: mentionGroups
      };
      
      const res = await commentsApi.create(requestId, data);
      setComments(prev => [...prev, res.data]);
      toast.success('Thread created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create thread');
    }
  };

  const handleReplyToThread = async (parentId, content, mentions, mentionGroups) => {
    try {
      const data = {
        content,
        document_id: documentId,
        parent_id: parentId,
        mentions,
        mention_groups: mentionGroups
      };
      
      const res = await commentsApi.create(requestId, data);
      // Add reply to the correct thread
      setComments(prev => prev.map(thread => {
        if (thread.id === parentId) {
          return { ...thread, replies: [...(thread.replies || []), res.data] };
        }
        return thread;
      }));
      toast.success('Reply posted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post reply');
    }
  };

  const handleMarkSeen = useCallback(async (commentId) => {
    try {
      await commentsApi.markSeen(commentId);
    } catch (error) {
      console.error('Failed to mark comment as seen:', error);
    }
  }, []);

  const handleResolve = async (commentId, resolved) => {
    try {
      await commentsApi.update(commentId, { is_resolved: resolved });
      fetchComments();
      toast.success(resolved ? 'Message resolved' : 'Message unresolved');
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const handleResolveThread = async (commentId, resolved) => {
    try {
      await commentsApi.resolveThread(commentId, resolved);
      fetchComments();
      toast.success(resolved ? 'Thread resolved' : 'Thread unresolved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update thread');
    }
  };

  // Document status handlers
  // iter134 — Coordinator manual-status confirmation modal state. When a Coordinator picks
  // "Approved" from the dropdown on a multi-assignee thread that still has pending peers,
  // pop a small confirm to choose between override (default) and "Approve from my side".
  const [coordStatusConfirm, setCoordStatusConfirm] = useState(null); // { documentId, newStatus, threadObj }

  const performStatusChange = async (documentId, newStatus, options = {}) => {
    try {
      await documentsApi.updateStatus(documentId, newStatus, options);
      fetchComments();
      if (options.as_override === false) {
        toast.info('Personal approval recorded. Doc thread stays "🔵 Pending Review" until the other assignees approve.');
      } else {
        toast.success(`Document status updated to ${newStatus.replace('_', ' ').toLowerCase()}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document status');
    }
  };

  const handleDocumentStatusChange = async (documentId, newStatus) => {
    // iter134/135 — intercept Coordinator's manual APPROVED change on a multi-assignee thread
    // and let them choose between override (default) and "Approve from my side".
    if (newStatus === 'APPROVED' && isCoordinator()) {
      const thread = comments.find((c) => c.document_id === documentId && c.is_document_thread && !c.parent_id);
      const assignees = thread?.document_status_assignees || [];
      const approvedBy = thread?.document_status_approved_by || [];
      const pendingPeers = assignees.filter((uid) => !approvedBy.includes(uid) && uid !== user?.id);
      if (assignees.length > 1 && pendingPeers.length > 0) {
        setCoordStatusConfirm({ documentId, newStatus, pendingPeerCount: pendingPeers.length });
        return;
      }
    }
    await performStatusChange(documentId, newStatus);
  };

  const handleAssignStatusReviewers = async (documentId, assigneeIds) => {
    try {
      await documentsApi.updateStatusAssignees(documentId, assigneeIds);
      fetchComments();
      toast.success('Reviewers assigned');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign reviewers');
    }
  };

  // Upload new version from thread
  // State for upload new version
  const [uploadingNewVersion, setUploadingNewVersion] = useState(false);
  const [uploadingNewVersionThreadId, setUploadingNewVersionThreadId] = useState(null);

  const handleUploadNewVersion = async (threadOrId) => {
    // iter110 — accept either a thread id (legacy) or the full thread object.
    // Bundle-chain threads route to the Generate Bundle dialog with the chain's
    // doc_type pre-selected & locked (sources must be re-picked each time).
    const isObj = threadOrId && typeof threadOrId === 'object';
    const threadId = isObj ? threadOrId.id : threadOrId;
    const t = isObj ? threadOrId : comments.find((c) => c.id === threadId);
    if (t && t.document_is_bundle_chain && onOpenBundleNewVersion) {
      onOpenBundleNewVersion(t.document_doc_type);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      setUploadingNewVersion(true);
      setUploadingNewVersionThreadId(threadId);
      try {
        const formData = new FormData();
        formData.append('file', file);
        await documentsApi.uploadNewVersionFromThread(threadId, formData);
        fetchComments();
        // Notify parent to refresh documents list
        if (onDocumentsChanged) onDocumentsChanged();
        toast.success('New version uploaded');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload new version');
      } finally {
        setUploadingNewVersion(false);
        setUploadingNewVersionThreadId(null);
      }
    };
    input.click();
  };

  // Download attachment
  const handleDownloadAttachment = async (documentId) => {
    try {
      const res = await documentsApi.getDownloadUrl(documentId);
      // The API returns download_url as a relative path, need to construct full URL
      const downloadUrl = res.data.download_url;
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = downloadUrl.startsWith('/') ? `${baseUrl}${downloadUrl}` : downloadUrl;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = fullUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to get download link');
    }
  };

  // iter117 — Multi-file: send ALL files in a SINGLE multipart POST so the
  // backend creates ONE comment carrying an attachments[] array (not N comments).
  const handleThreadAttachment = async (threadId, content, mentions, mentionGroups, fileOrFiles, relatedVersion, notifyUploader = true) => {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    if (files.length === 0) return;

    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      formData.append('content', content || '');
      formData.append('mentions', JSON.stringify(mentions || []));
      formData.append('mention_groups', JSON.stringify(mentionGroups || []));
      if (relatedVersion) {
        formData.append('related_to_version', relatedVersion.toString());
      }
      // iter167 Issue 16 — Tell the backend whether to auto-add the original
      // uploader to mentions / visible_to. `false` when the user explicitly
      // dismissed the auto-mention chip in the composer.
      formData.append('notify_uploader', notifyUploader ? 'true' : 'false');

      const res = await documentsApi.uploadThreadAttachment(threadId, formData);
      fetchComments();
      if (onDocumentsChanged) onDocumentsChanged();

      if (res?.data?.auto_status_changed === 'CHANGES_REQUESTED') {
        toast.success("Thread status updated to 'Changes Requested'. Use the status button on the thread header to change it.", {
          duration: 6000,
        });
        // iter116 — pulse the thread-header status button for 6s so the user
        // visually finds the change-status control without an extra modal.
        setPulseStatusHintForThreadId(threadId);
        setTimeout(() => setPulseStatusHintForThreadId((cur) => (cur === threadId ? null : cur)), 6500);
      } else {
        toast.success(files.length > 1 ? `${files.length} files attached` : 'File attached');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to attach file');
    }
  };

  // Handle editing attachment's related version
  const handleEditAttachmentVersion = async (documentId, newVersion) => {
    try {
      await documentsApi.updateAttachmentRelatedVersion(documentId, newVersion);
      fetchComments();
      // Notify parent to refresh documents list
      if (onDocumentsChanged) onDocumentsChanged();
      toast.success(`Attachment now related to version ${newVersion}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update version');
    }
  };

  const handleDelete = async (commentId) => {
    if (!confirm('Delete this message? If it is the start of a thread, all replies will be deleted too. This cannot be undone.')) return;

    try {
      await commentsApi.delete(commentId);
      fetchComments();
      toast.success('Deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleEdit = async (commentId, content) => {
    try {
      await commentsApi.update(commentId, { content });
      fetchComments();
      toast.success('Updated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  // Apply doc-status filter (if set to a DocumentStatus value).
  // 'all'         → show every thread (doc + regular)
  // 'unresolved'  → show only unresolved (legacy)
  // 'APPROVED' / 'CHANGES_REQUESTED' / 'PENDING_REVIEW' → show ONLY doc threads
  //                 whose attached document is in that status.
  const isDocStatusFilter = ['APPROVED', 'CHANGES_REQUESTED', 'PENDING_REVIEW'].includes(filter);
  const statusFilteredComments = isDocStatusFilter
    ? comments.filter(c => c.is_document_thread && c.document_status === filter)
    : comments;
  // Apply doc-type filter on top (iter107). When the user picks a specific doc type,
  // only document threads matching that type survive — non-document threads are hidden
  // because the chip is type-scoped by definition.
  const availableDocTypesInThreads = Array.from(new Set(
    comments
      .filter(c => c.is_document_thread && c.document_doc_type)
      .map(c => c.document_doc_type)
  )).sort((a, b) => a.localeCompare(b));
  const filteredComments = (docTypeFilter === 'all'
    ? statusFilteredComments
    : statusFilteredComments.filter(c => c.is_document_thread && c.document_doc_type === docTypeFilter)
  ).filter(c => {
    // iter117 — thread-type filter
    if (threadTypeFilter === 'document') return c.is_document_thread;
    if (threadTypeFilter === 'general') return !c.is_document_thread;
    return true;
  });
  // Separate resolved and unresolved threads
  const unresolvedThreads = filteredComments.filter(c => !c.is_thread_resolved);
  const resolvedThreads = filteredComments.filter(c => c.is_thread_resolved);

  return (
    <div className="space-y-4" data-testid="discussion-thread">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          {title}
          {comments.length > 0 && (
            <Badge variant="outline">{comments.length}</Badge>
          )}
        </h3>
        
        <div className="flex items-center gap-2">
          {/* iter117 — Thread-type filter (Document vs General) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1" data-testid="thread-type-filter-button">
                <MessageSquare className="w-4 h-4" />
                {threadTypeFilter === 'all' ? 'All Threads'
                  : threadTypeFilter === 'document' ? '📄 Documents'
                  : '💬 General'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setThreadTypeFilter('all')} data-testid="thread-type-filter-all">
                All Threads
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setThreadTypeFilter('document')} data-testid="thread-type-filter-document">
                📄 Documents
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setThreadTypeFilter('general')} data-testid="thread-type-filter-general">
                💬 General
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Doc-type filter (iter107) — independently filters threads by their attached
              document's doc_type. Only shows when at least 2 distinct doc types exist
              across the request's document threads. */}
          {availableDocTypesInThreads.length >= 2 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1" data-testid="thread-doctype-filter-button">
                  <FileText className="w-4 h-4" />
                  {docTypeFilter === 'all' ? 'All Types' : docTypeFilter}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
                <DropdownMenuItem onClick={() => setDocTypeFilter('all')} data-testid="thread-doctype-filter-all">
                  All Types
                </DropdownMenuItem>
                {availableDocTypesInThreads.map((dt) => (
                  <DropdownMenuItem
                    key={dt}
                    onClick={() => setDocTypeFilter(dt)}
                    data-testid={`thread-doctype-filter-${dt}`}
                  >
                    {dt}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* Thread filter — doc threads filter by document status */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1" data-testid="thread-filter-button">
                <Filter className="w-4 h-4" />
                {filter === 'all' ? 'All'
                  : filter === 'APPROVED' ? '🟢 Approved'
                  : filter === 'CHANGES_REQUESTED' ? '🟡 Changes Requested'
                  : filter === 'PENDING_REVIEW' ? '🔵 Pending Review'
                  : 'All'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilter('all')} data-testid="thread-filter-all">
                All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter('PENDING_REVIEW')} data-testid="thread-filter-pending">
                🔵 Pending Review
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter('CHANGES_REQUESTED')} data-testid="thread-filter-changes">
                🟡 Changes Requested
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter('APPROVED')} data-testid="thread-filter-approved">
                🟢 Approved
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* New Thread Input - AT THE TOP (no label) - Hidden when readOnly */}
      {!readOnly && (
        <div className="pb-4 border-b border-slate-200">
          <CommentInput
            onSubmit={handleSubmitNewThread}
            mentionData={mentionData}
            placeholder="Write a message to start a new thread..."
          />
        </div>
      )}

      {/* Threads List */}
      {comments.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-20" />
          <p>No threads yet. Start a discussion above!</p>
        </div>
      ) : filteredComments.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Filter className="w-10 h-10 mx-auto mb-2 opacity-20" />
          <p className="text-sm">No threads match this filter.</p>
          <button onClick={() => setFilter('all')} className="text-blue-600 hover:underline text-sm mt-1" data-testid="thread-filter-clear">
            Clear filter
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Unresolved threads first */}
          {unresolvedThreads.map(thread => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              onResolve={handleResolve}
              onResolveThread={handleResolveThread}
              onDelete={handleDelete}
              onEdit={handleEdit}
              onReply={handleReplyToThread}
              onMarkSeen={handleMarkSeen}
              onStatusChange={handleDocumentStatusChange}
              onAssignStatusReviewers={handleAssignStatusReviewers}
              onUploadNewVersion={thread.is_document_thread ? handleUploadNewVersion : null}
              uploadingNewVersion={uploadingNewVersion && uploadingNewVersionThreadId === thread.id}
              onDownloadAttachment={handleDownloadAttachment}
              onPreviewDoc={onPreviewDoc}
              onAttachFile={(content, mentions, groups, file, version, notifyUploader) => handleThreadAttachment(thread.id, content, mentions, groups, file, version, notifyUploader)}
              onEditAttachmentVersion={handleEditAttachmentVersion}
              currentUserId={user?.id}
              mentionData={mentionData}
              canManageStatus={canManageStatus}
              assignedTeam={assignedTeam}
              documentVersions={thread.document_versions || []}
              isOverrideRole={isOverrideViewer}
              requestId={requestId}
              fetchThreadMentionData={fetchThreadMentionData}
              readOnly={readOnly}
              highlightedThreadId={highlightedThreadId}
              userHasApproved={userHasApproved}
              pulseStatusHint={pulseStatusHintForThreadId === thread.id}
            />
          ))}
          
          {/* Resolved threads (also shown when a doc-status filter is active so
              approved/changes-requested doc threads aren't hidden) */}
          {resolvedThreads.length > 0 && (filter === 'all' || isDocStatusFilter) && (
            <>
              {filter === 'all' && unresolvedThreads.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-slate-500 my-4">
                  <div className="flex-1 h-px bg-slate-200"></div>
                  <span>Resolved Threads ({resolvedThreads.length})</span>
                  <div className="flex-1 h-px bg-slate-200"></div>
                </div>
              )}
              {resolvedThreads.map(thread => (
                <ThreadCard
                  key={thread.id}
                  thread={thread}
                  onResolve={handleResolve}
                  onResolveThread={handleResolveThread}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onReply={handleReplyToThread}
                  onMarkSeen={handleMarkSeen}
                  onStatusChange={handleDocumentStatusChange}
                  onAssignStatusReviewers={handleAssignStatusReviewers}
                  onUploadNewVersion={thread.is_document_thread ? handleUploadNewVersion : null}
                  uploadingNewVersion={uploadingNewVersion && uploadingNewVersionThreadId === thread.id}
                  onDownloadAttachment={handleDownloadAttachment}
                  onPreviewDoc={onPreviewDoc}
                  onAttachFile={(content, mentions, groups, file, version, notifyUploader) => handleThreadAttachment(thread.id, content, mentions, groups, file, version, notifyUploader)}
                  onEditAttachmentVersion={handleEditAttachmentVersion}
                  currentUserId={user?.id}
                  mentionData={mentionData}
                  canManageStatus={canManageStatus}
                  assignedTeam={assignedTeam}
                  documentVersions={thread.document_versions || []}
                  isOverrideRole={isOverrideViewer}
                  requestId={requestId}
                  fetchThreadMentionData={fetchThreadMentionData}
                  readOnly={readOnly}
                  highlightedThreadId={highlightedThreadId}
                  userHasApproved={userHasApproved}
                  pulseStatusHint={pulseStatusHintForThreadId === thread.id}
                />
              ))}
            </>
          )}
        </div>
      )}

      {/* iter134 — Coordinator's manual APPROVED status change on a multi-assignee thread.
          Two-option modal so they explicitly pick override vs per-assignee. */}
      <Dialog
        open={!!coordStatusConfirm}
        onOpenChange={(open) => { if (!open) setCoordStatusConfirm(null); }}
      >
        <DialogContent className="max-w-lg" data-testid="coord-status-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              Approve this document thread?
            </DialogTitle>
            <DialogDescription>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold mr-1">
                <Users className="w-3 h-3" />
                {coordStatusConfirm?.pendingPeerCount} other assignee
                {coordStatusConfirm?.pendingPeerCount === 1 ? '' : 's'}
              </span>
              still pending. As the Coordinator you can either bypass them or approve only from your side.
            </DialogDescription>
          </DialogHeader>
          {/* iter143 — visual polish: each option becomes a self-contained card with an icon,
              color cue, and full-bleed action button so the operator instantly groks the tradeoff. */}
          <div className="grid gap-3 my-2">
            <button
              type="button"
              data-testid="coord-status-confirm-mine"
              className="text-left rounded-lg border-2 border-blue-200 bg-blue-50/40 hover:border-blue-400 hover:bg-blue-50 px-4 py-3 transition-colors"
              onClick={async () => {
                const c = coordStatusConfirm;
                setCoordStatusConfirm(null);
                await performStatusChange(c.documentId, c.newStatus, { as_override: false });
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-blue-900">Approve from my side</span>
                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">Safer</Badge>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Only your slot ticks. Doc thread stays <span className="text-blue-700 font-medium">&ldquo;🔵 Pending Review / Changes Requested&rdquo;</span> until the other assignees approve from their side too.
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              data-testid="coord-status-confirm-override"
              className="text-left rounded-lg border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100/50 hover:border-emerald-500 hover:from-emerald-100 hover:to-emerald-100 px-4 py-3 transition-colors shadow-sm"
              onClick={async () => {
                const c = coordStatusConfirm;
                setCoordStatusConfirm(null);
                await performStatusChange(c.documentId, c.newStatus, { as_override: true });
              }}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0 shadow-inner">
                  <ShieldCheck className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-emerald-900">Approve &amp; bypass all other assignees</span>
                    <Badge className="bg-emerald-600 text-white text-[10px]">Override</Badge>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Doc thread flips to <span className="text-emerald-700 font-medium">Approved</span> immediately. Recorded as a Coordinator override in the audit trail. Bypassed assignees are auto-@mentioned.
                  </p>
                </div>
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoordStatusConfirm(null)} data-testid="coord-status-confirm-cancel">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Inline emerald audit banner shown inside a doc thread when the source doc is signed
 * (standalone OR via a bundle). Authored by the signer but rendered as a system-style
 * row — no avatar, no reply/edit/delete actions, no seen-by chips.
 */
function SignedMarkerBanner({ reply, onPreviewDoc }) {
  // Render markdown-ish bold (**X**) and code (`X`) in the content
  const renderContent = (text) => {
    if (!text) return null;
    const parts = [];
    let i = 0;
    const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > i) parts.push(text.slice(i, m.index));
      const tok = m[0];
      if (tok.startsWith('**')) parts.push(<strong key={m.index} className="text-emerald-900">{tok.slice(2, -2)}</strong>);
      else if (tok.startsWith('`')) parts.push(<code key={m.index} className="bg-emerald-100 px-1 py-0.5 rounded text-[11px] text-emerald-900">{tok.slice(1, -1)}</code>);
      i = m.index + tok.length;
    }
    if (i < text.length) parts.push(text.slice(i));
    return parts;
  };
  // iter148g — clickable signed-doc marker. Clicking anywhere on the banner
  // opens the signed PDF in the existing preview modal. Falls back to a no-op
  // when the marker pre-dates iter148g (no signed_document_id stored).
  const targetDocId = reply.signed_document_id;
  const isClickable = !!targetDocId && typeof onPreviewDoc === 'function';
  const handleClick = () => {
    if (!isClickable) return;
    // iter148h — onPreviewDoc accepts a documentId (string), not an object — match
    // the other call-sites (MessageItem, ThreadCard) so the preview modal opens.
    onPreviewDoc(targetDocId);
  };
  return (
    <div
      className={`flex items-start gap-2.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2.5 ${isClickable ? 'cursor-pointer hover:bg-emerald-100 transition-colors' : ''}`}
      data-comment-id={reply.id}
      data-testid={`signed-marker-${reply.id}`}
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      title={isClickable ? 'Click to preview the signed document' : undefined}
    >
      <BadgeCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
      <p className="text-sm text-emerald-900 leading-snug">
        {renderContent(reply.content)}
      </p>
    </div>
  );
}



/**
 * iter148g — Collapsed group of consecutive ClinicalFlow Agent messages.
 * Default state: shows a single chip with the latest agent message snippet;
 * click to expand all grouped agent messages inline.
 */
function AgentMessagesGroup({
  replies,
  onMarkSeen,
  onPreviewDoc,
  currentUserId,
  isThreadResolved,
  documentVersions,
  isOverrideRole,
  hideSeenForCompany,
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60" data-testid={`agent-group-${replies[0].id}`}>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-emerald-800 hover:bg-emerald-100/70 rounded-lg transition-colors"
        data-testid={`agent-group-toggle-${replies[0].id}`}
      >
        <span className="flex items-center gap-2">
          <BadgeCheck className="w-4 h-4 text-emerald-600" />
          {expanded
            ? `${replies.length} partial-approval update${replies.length > 1 ? 's' : ''} — hide`
            : `${replies.length} partial-approval update${replies.length > 1 ? 's' : ''} — expand`}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded ? (
        <div className="px-3 pb-3 space-y-2">
          {replies.map(r => (
            <MessageItem
              key={r.id}
              message={r}
              onMarkSeen={onMarkSeen}
              onPreviewDoc={onPreviewDoc}
              currentUserId={currentUserId}
              isThreadResolved={isThreadResolved}
              documentVersions={documentVersions}
              isOverrideRole={isOverrideRole}
              hideSeenForCompany={hideSeenForCompany}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
